import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Sparkles, ChevronDown, Code2, Zap, CheckCircle2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { useTypingAnimation } from "@/hooks/useTypingAnimation";
import { requestAccessCsv } from "@/lib/utils";

const emailSchema = z.string().trim().email({ message: "Please enter a valid email address" }).max(255);

const terminalLines = [
  { text: "$ healion analyze --scan-errors", delay: 0, type: "command" as const },
  { text: "⠋ Scanning production errors...", delay: 800, type: "spinner" as const },
  { text: "✓ 1 critical bug detected", delay: 1800, type: "success" as const },
  { text: "⠋ Generating autofix...", delay: 2400, type: "spinner" as const },
  { text: "", delay: 3200, type: "empty" as const },
  { text: "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━", delay: 3400, type: "divider" as const },
  { text: "BugFix[Healion] : Autofix: AbortError", delay: 3600, type: "header" as const },
  { text: "Opened by: Healion  ·  View PR →", delay: 3900, type: "link" as const },
  { text: "Priority: Medium  ·  Branch: autofix-aborterror → main", delay: 4200, type: "meta" as const },
  { text: "", delay: 4500, type: "empty" as const },
  { text: "Problem", delay: 4700, type: "label" as const },
  { text: "  signal is aborted without reason", delay: 4900, type: "detail" as const },
  { text: "Root Cause", delay: 5200, type: "label" as const },
  { text: "  triggerAbortRace() (Navbar.jsx:183–188) <more>", delay: 5400, type: "detail" as const },
  { text: "Solution", delay: 5700, type: "label" as const },
  { text: "  Replaced void p; with void p.catch(…) <more>", delay: 5900, type: "detail" as const },
  { text: "Confidence: 97/100 ██████████▊", delay: 6200, type: "confidence" as const },
  { text: "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━", delay: 6500, type: "divider" as const },
  { text: "", delay: 6700, type: "empty" as const },
  { text: "🎉 Bug squashed. 0 errors remaining.", delay: 6900, type: "final" as const },
  { text: "   You just saved 4.5 hours. Grab a coffee ☕", delay: 7400, type: "comment" as const },
];

const TerminalSuccess = () => {
  const [visibleLines, setVisibleLines] = useState<number>(0);

  useEffect(() => {
    const timers = terminalLines.map((line, i) =>
      setTimeout(() => setVisibleLines(i + 1), line.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="w-full max-w-3xl mx-auto rounded-xl border border-border bg-card overflow-hidden shadow-lg animate-scale-in">
      {/* Terminal title bar */}
      <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b border-border">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-destructive/70" />
          <span className="w-3 h-3 rounded-full bg-amber-500/70" />
          <span className="w-3 h-3 rounded-full bg-green-500/70" />
        </div>
        <span className="text-xs text-muted-foreground font-mono ml-2">healion — zsh</span>
      </div>
      {/* Terminal body */}
      <div className="px-5 sm:px-6 py-4 font-mono text-[11px] sm:text-xs leading-relaxed min-h-[200px]">
        {terminalLines.slice(0, visibleLines).map((line, i) => (
          <div
            key={i}
            className={`transition-opacity duration-300 ${
              line.type === "command" ? "text-foreground font-semibold" :
              line.type === "spinner" ? "text-muted-foreground" :
              line.type === "success" ? "text-green-500" :
              line.type === "header" ? "text-accent font-bold text-xs sm:text-sm" :
              line.type === "link" ? "text-cyan-400 underline cursor-pointer" :
              line.type === "meta" ? "text-muted-foreground text-[10px] sm:text-[11px]" :
              line.type === "label" ? "text-accent/80 font-semibold mt-1" :
              line.type === "detail" ? "text-foreground/70" :
              line.type === "confidence" ? "text-green-400 font-bold" :
              line.type === "divider" ? "text-border/50" :
              line.type === "final" ? "text-accent font-bold text-xs sm:text-sm" :
              line.type === "comment" ? "text-muted-foreground italic" :
              ""
            }`}
          >
            {line.text}
          </div>
        ))}
        {visibleLines < terminalLines.length && (
          <span className="animate-terminal-cursor text-foreground">▊</span>
        )}
      </div>
    </div>
  );
};

export const HeroSection = () => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const { displayedText } = useTypingAnimation("that fixes your production bugs", 80);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 animate-float">
          <Code2 className="w-12 h-12 text-accent/10" />
        </div>
        <div className="absolute bottom-32 right-20 animate-float" style={{ animationDelay: '1s' }}>
          <Zap className="w-16 h-16 text-accent/10" />
        </div>
        <div className="absolute top-1/3 right-10 text-accent/5 font-mono text-sm animate-code-blink">
          {'{ fix: "auto" }'}
        </div>
        <div className="absolute bottom-1/3 left-20 text-accent/5 font-mono text-sm animate-code-blink" style={{ animationDelay: '0.5s' }}>
          {'// TODO: never'}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-24 sm:py-32 text-center">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-8 sm:mb-10 px-4 font-heading">
          <span className="metallic-text">The AI teammate</span><br />
          {displayedText}
          <span className="animate-terminal-cursor">|</span>
        </h1>

        <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-12 sm:mb-16 max-w-4xl mx-auto px-4 font-light leading-relaxed">
          Healion AI analyzes errors, prioritizes them, generates fixes, and pushes PRs to your Repo automatically.
          From detection to deployment in <span className="text-accent font-medium">15 minutes</span>.
        </p>

        {/* Waitlist Form */}
        <div className="flex flex-col items-center gap-4 mb-8 sm:mb-12 max-w-lg mx-auto px-4">
          {!isSubmitted ? (
            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 flex-1 bg-background border-border text-foreground placeholder:text-muted-foreground"
              />
              <Button 
                onClick={async () => {
                  if (isSubmitted) return;
                  const result = emailSchema.safeParse(email);
                  if (!result.success) {
                    toast({
                      variant: "destructive",
                      title: "Invalid email",
                      description: result.error.errors[0]?.message || "Please enter a valid email address",
                      duration: 5000,
                    });
                    return;
                  }
                  try {
                    const response: any = await requestAccessCsv(email, 'Homepage Hero');
                    if (response && response?.status && response?.status >= 400) {
                      throw new Error(response.statusText);
                    }
                    setIsSubmitted(true);
                    toast({
                      title: "🎉 You're on the list!",
                      description: "Welcome aboard! Your bugs don't stand a chance.",
                      duration: 5000,
                    });
                  } catch (err) {
                    console.error(err);
                    toast({
                      variant: "destructive",
                      title: "Error",
                      description: "Please refresh the page and try again.",
                      duration: 5000,
                    });
                  }
                }}
                variant="accent" 
                size="lg"
                className="h-12 px-8 shadow-accent hover:scale-105 transition-transform"
              >
                {isSubmitted ? "Joined" : "Join Waitlist"}
                {isSubmitted ? <CheckCircle2 className="w-4 h-4 ml-2" /> : <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          ) : (
            <div className="w-full space-y-4">
              {/* Success header */}
              <div className="flex flex-col items-center justify-center gap-1 text-accent">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6" />
                  <span className="font-heading font-bold text-lg">You're in, bug slayer! 🚀</span>
                </div>
                <p className="text-xs text-muted-foreground/70 italic">
                  We'll ping you when it's your turn. Until then, your bugs are on borrowed time.
                </p>
              </div>
              <p className="text-sm text-muted-foreground font-bold">
                Here's a sneak peek of what Healion will do for you:
              </p>
              {/* Terminal animation */}
              <div className="text-left">
                <TerminalSuccess />
              </div>
            </div>
          )}
        </div>

        {/* 100 free fixes highlight */}
        <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 rounded-full border border-accent/30 bg-accent/5 mx-4">
          <Sparkles className="w-4 h-4 text-accent" />
          <span className="text-sm sm:text-base font-medium text-foreground/90">
            <span className="text-accent font-bold">20 free code fixes</span> — save 100 hours
          </span>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-2 animate-bounce-slow">
        <span className="text-xs text-muted-foreground font-mono">scroll to explore</span>
        <ChevronDown className="w-5 h-5 text-accent" />
      </div>
    </section>
  );
};
